'use client';

import React from 'react';
import { Group, Text, Badge } from '@mantine/core';
import { IconBrain, IconMapPin, IconIdBadge2, IconUserCircle } from '@tabler/icons-react';

/* ------------------------------------------------------------------ */
/* Props                                                              */
/* ------------------------------------------------------------------ */
interface FiltersSummaryProps {
  filters: {
    expertise?: string | string[];
    location?: string;
    name?: string;
    bio?: string;
  };
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */
export default function FiltersSummary({ filters }: FiltersSummaryProps) {
  /* ---------- expertise list → badges ---------- */
  const expertiseArr =
    typeof filters.expertise === 'string'
      ? [filters.expertise]
      : Array.isArray(filters.expertise)
      ? filters.expertise
      : [];

  const expertiseBadges = expertiseArr.map((exp) => (
    <Badge
      key={`expertise-${exp}`}
      variant="light"
      color="info"
      mx={4}
      px="md"
      size="lg"
      leftSection={<IconBrain size={16} />}
      style={{ textTransform: 'none' }}
    >
      {exp}
    </Badge>
  ));

  /* ---------- other badges ---------- */
  const locationBadge = filters.location ? (
    <Badge
      key="location"
      variant="light"
      color="info"
      mx={4}
      px="md"
      size="lg"
      leftSection={<IconMapPin size={16} />}
      style={{ textTransform: 'none' }}
    >
      {filters.location}
    </Badge>
  ) : null;

  const nameBadge = filters.name ? (
    <Badge
      key="name"
      variant="light"
      color="info"
      mx={4}
      px="md"
      size="lg"
      leftSection={<IconIdBadge2 size={16} />}
      style={{ textTransform: 'none' }}
    >
      {filters.name}
    </Badge>
  ) : null;

  const bioBadge = filters.bio ? (
    <Badge
      key="bio"
      variant="light"
      color="info"
      mx={4}
      px="md"
      size="lg"
      leftSection={<IconUserCircle size={16} />}
      style={{ textTransform: 'none' }}
    >
      {filters.bio}
    </Badge>
  ) : null;

  /* ---------- assemble badge parts ---------- */
  const parts: React.ReactNode[] = [];

  if (expertiseBadges.length > 0) {
    parts.push(...expertiseBadges);
  }

  if (locationBadge) {
    parts.push(locationBadge);
  }

  if (nameBadge) {
    parts.push(nameBadge);
  }

  if (bioBadge) {
    parts.push(bioBadge);
  }

  /* ---------- helper – render badges with clean spacing ---------- */
  const renderParts = (items: React.ReactNode[]): React.ReactNode => {
    if (items.length === 0) return null;
    return items;
  };

  if (parts.length === 0) return null;

  /* ---------- render ---------- */
  return (
    <Group justify="center" align="center" mt="lg" mb="sm" gap={0}>
      <Text component="span" size="lg" fw={500} ta="center" mr="xs" style={{ position: 'relative', top: '-1px' }}>
        Filters:
      </Text>
      {renderParts(parts)}
    </Group>
  );
}
